import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import FormControl from "@mui/material/FormControl"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import OutlinedInput from "@mui/material/OutlinedInput"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import * as React from "react"
import { makeStyles } from "tss-react/mui"

import {
    fetchMerchantBillReceivers,
    FetchMerchantBillReceiversResponse,
    MerchantQuery,
} from "../../../../../merchant/admin/merchantAdminApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MenuProps } from "../../../../../../shared/components/filters/constants/filterMenuConst"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"

type MerchantBillReceiversFilterProps = Readonly<{
    value: string[]
    setValue: (value: string[]) => void
    merchantId?: string
    customerId?: string
}>

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

function transformData(data: FetchMerchantBillReceiversResponse) {
    return data.billReceivers
}

export function MerchantBillReceiversFilter({
    merchantId,
    value,
    setValue,
    customerId,
}: MerchantBillReceiversFilterProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const isCompanySelected = Boolean(customerId)
    const isDisabled = !Boolean(merchantId) || !isCompanySelected

    const { data } = useApiQuery(fetchMerchantBillReceivers, {
        queryName: MerchantQuery.BILL_RECEIVERS_LIST,
        dependencies: {
            merchantId: merchantId ? +merchantId : 0,
        },
        enabled: Boolean(merchantId) && isCompanySelected,
        select: transformData,
    })

    const billReceiverMap = React.useMemo(() => {
        const map: { [id: string]: string } = {}
        data?.forEach((receiver) => {
            map[receiver.id] = receiver.displayName
        })
        return map
    }, [data])

    const selectedNames = value.map((id) => billReceiverMap[id] || "")

    const handleChange = (event: SelectChangeEvent<typeof selectedNames>) => {
        const {
            target: { value },
        } = event

        const selectedValues = typeof value === "string" ? value.split(",") : value
        const selectedIdValues = selectedValues
            .map((name) => Object.keys(billReceiverMap).find((key) => billReceiverMap[key] === name))
            .filter((id): id is string => id !== undefined)

        setValue(selectedIdValues)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="supplier-meeting-service-drawer-bill-receivers-label">
                <InputLabel id="multiple-checkbox-bill-receivers-label">Bill receivers</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-bill-receivers-label"
                id="multiple-checkbox-bill-receivers"
                multiple
                disabled={isDisabled}
                value={selectedNames}
                onChange={handleChange}
                input={<OutlinedInput label="Bill receivers" />}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={MenuProps}
            >
                {data?.map((receiver) => (
                    <MenuItem key={receiver.id} value={receiver.displayName}>
                        <Checkbox checked={selectedNames.indexOf(receiver.displayName) > -1} />
                        <ListItemText primary={receiver.displayName} />
                    </MenuItem>
                ))}
            </Select>
            {!isCompanySelected && (
                <FormHelperText>
                    {l10n.getString("supplier-meeting-service-drawer-bill-receivers-select-company-helper-text")}
                </FormHelperText>
            )}
        </FormControl>
    )
}
